import React, { useEffect, useState } from 'react';
import { CalendarDays, CircleCheck, MapPin, Play, ShieldCheck, Ticket } from 'lucide-react';
import { CartItem, useCart } from '../contexts/CartContext';
import CartView from './CartView';
import { ClassData, getFullImageUrl } from '../services/api';
import { getCurrentUser } from '../utils/userStorage';
import Header from './Header';
import EventLikeButton from './EventLikeButton';
import { useScrollToTop } from '../hooks/useScrollToTop';
import {
  getPeekPassEventDate,
  getPeekPassOrganizer,
  getPeekPassSummary,
  getPeekPassTitle,
  getPeekPassVenue,
  getPeekPassVenueAddress,
  getPeekPassVenueCity,
  mapCategoryToEvent,
  mapDifficultyToAvailability,
  mapDifficultyToExperience,
  PEEKPASS_BRAND
} from '../utils/peekPassBrand';

export interface ClassPack {
  _id?: string;
  id?: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  difficulty: string;
  duration: string | number;
  instructor?: string;
  image?: string;
  videoUrl?: string;
  benefits: string[];
  requirements: string[];
  exercises: string[];
  organizer?: { id: string; name: string; avatar: string; rating: number; clients: number; country: string };
  thumbnail?: string;
  reelUrl?: string;
  discount?: number;
  isFallback?: boolean;
}

interface SingleCardProps {
  pack?: ClassData;
  userEmail?: string;
  onGoBack: () => void;
  onPurchase?: (packId: string) => void;
  onRegister: () => void;
  isCartMode?: boolean;
  shouldAutoplay?: boolean;
}

const formatPrice = (price: number, currency: string) => new Intl.NumberFormat('es-AR', {
  style: 'currency', currency, maximumFractionDigits: 2
}).format(price);

const SingleCard: React.FC<SingleCardProps> = ({ pack, userEmail, onGoBack, onPurchase, onRegister, isCartMode = false, shouldAutoplay = false }) => {
  const { addToCart } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  useScrollToTop();

  useEffect(() => {
    const handlePopState = () => onGoBack();
    window.history.pushState({ page: 'peekpass-event' }, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onGoBack]);

  useEffect(() => {
    if (!shouldAutoplay || !pack?.reelUrl || !videoRef.current) return;
    videoRef.current.muted = true;
    videoRef.current.play().catch(() => undefined);
  }, [pack?.reelUrl, shouldAutoplay]);

  if (isCartMode) return <CartView onGoBack={onGoBack} />;

  if (!pack) {
    return <div className="min-h-screen bg-[#f8f5ef]"><Header showBackButton onBack={onGoBack} title="Evento" /><main className="grid min-h-[70vh] place-items-center px-4"><div className="text-center"><h1 className="text-3xl font-black">Evento no encontrado</h1><button type="button" onClick={onGoBack} className="mt-5 bg-black px-5 py-3 text-sm font-black text-white">Volver a la agenda</button></div></main></div>;
  }

  const title = getPeekPassTitle(pack);
  const category = mapCategoryToEvent(pack.category);
  const mode = mapDifficultyToExperience(pack.difficulty);
  const availability = mapDifficultyToAvailability(pack.difficulty);
  const summary = getPeekPassSummary(pack);
  const venue = getPeekPassVenue(pack);
  const venueAddress = getPeekPassVenueAddress(pack);
  const organizer = getPeekPassOrganizer(pack.organizer?.name);
  const discount = typeof pack.discount === 'number' ? pack.discount : 0;
  const price = discount ? pack.price * (1 - discount / 100) : pack.price;
  const eventDate = getPeekPassEventDate(pack);
  const day = new Intl.DateTimeFormat('es-AR', { day: '2-digit' }).format(eventDate);
  const month = new Intl.DateTimeFormat('es-AR', { month: 'short' }).format(eventDate).replace('.', '').toUpperCase();
  const year = new Intl.DateTimeFormat('es-AR', { year: 'numeric' }).format(eventDate);
  const city = getPeekPassVenueCity(pack);

  const handleAddToCart = () => {
    if (pack.isFallback) { alert('Este evento se está actualizando. Volvé a intentar en unos minutos.'); return; }
    if (!getCurrentUser()) { onRegister(); return; }
    const item: CartItem = {
      id: pack._id || pack.title,
      title,
      description: summary,
      price,
      quantity: 1,
      originalPrice: pack.price,
      discountPercent: discount,
      currency: pack.currency,
      category,
      difficulty: mode,
      duration: Number.parseInt(pack.duration, 10) || 0,
      instructor: organizer,
      image: pack.thumbnail,
      videoUrl: pack.reelUrl,
      benefits: pack.benefits || [],
      requirements: pack.requirements || [],
      exercises: pack.exercises || [],
      eventDate: `${day} ${month} ${year}`,
      venue,
      organizerId: pack.organizer?.id,
      courseId: pack._id || pack.title
    };
    setIsAddingToCart(true);
    addToCart(item);
    setIsAddingToCart(false);
    onPurchase?.('cart');
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] text-slate-950">
      <Header showBackButton onBack={onGoBack} title={title}>{userEmail ? <span className="hidden text-sm font-semibold text-white/70 xl:inline">{userEmail}</span> : null}</Header>
      <main className="w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="mx-auto grid max-w-[1440px] gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section>
            <div className="relative overflow-hidden bg-black shadow-[0_28px_70px_-42px_rgba(15,23,42,0.58)]">
              <div className="relative aspect-[16/9]">
                {pack.reelUrl ? <video ref={videoRef} className="h-full w-full object-cover" poster={getFullImageUrl(pack.thumbnail)} controls playsInline muted><source src={getFullImageUrl(pack.reelUrl)} type="video/mp4" /></video> : pack.thumbnail ? <img src={getFullImageUrl(pack.thumbnail)} alt={title} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center bg-slate-900 text-white"><Play className="h-10 w-10" /></div>}
                <div className="absolute left-4 top-4 bg-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.15em] text-black">{category}</div>
                <EventLikeButton event={pack} onRequireLogin={onRegister} className="absolute right-4 top-4 bg-white/95 shadow-lg" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#dff38c]" />
            </div>

            <div className="grid gap-7 border-b border-slate-200 py-8 sm:grid-cols-[104px_minmax(0,1fr)]">
              <div className="flex border-l-4 border-[#71931b] pl-4 sm:block sm:border-l-0 sm:border-r sm:border-slate-200 sm:pl-0 sm:pr-6 sm:text-center">
                <span className="text-5xl font-black leading-none tracking-[-0.08em]">{day}</span><div className="ml-3 mt-1 sm:ml-0 sm:mt-2"><span className="block text-sm font-black tracking-[0.14em] text-[#71931b]">{month}</span><span className="block text-xs font-bold text-slate-500">{year}</span></div>
              </div>
              <div><p className="text-xs font-black uppercase tracking-[0.23em] text-[#71931b]">{mode} · {availability}</p><h1 className="mt-3 text-4xl font-black tracking-[-0.065em] sm:text-5xl">{title}</h1><p className="mt-5 max-w-3xl text-base leading-7 text-slate-600">{summary}</p></div>
            </div>

            <div className="grid gap-px bg-slate-200 sm:grid-cols-3">
              <div className="bg-white p-5"><MapPin className="h-5 w-5 text-[#71931b]" /><p className="mt-4 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Location</p><p className="mt-1 font-black">{venue}</p></div>
              <div className="bg-white p-5"><CalendarDays className="h-5 w-5 text-[#71931b]" /><p className="mt-4 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Ciudad</p><p className="mt-1 font-black">{city}</p></div>
              <div className="bg-white p-5"><Ticket className="h-5 w-5 text-[#71931b]" /><p className="mt-4 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Organiza</p><p className="mt-1 font-black">{organizer || PEEKPASS_BRAND}</p></div>
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <section className="border border-slate-200 bg-white p-6"><h2 className="text-xl font-black">Tu entrada incluye</h2><div className="mt-5 space-y-3">{(pack.benefits || ['Acceso digital confirmado', 'Información del evento en tu reserva']).map((benefit, index) => <p key={`${benefit}-${index}`} className="flex gap-3 text-sm leading-6 text-slate-700"><CircleCheck className="mt-1 h-4 w-4 shrink-0 text-[#71931b]" />{benefit}</p>)}</div></section>
              <section className="border border-slate-200 bg-white p-6"><h2 className="text-xl font-black">Información útil</h2><div className="mt-5 space-y-3">{(pack.requirements || ['Presentá tu entrada digital al ingresar']).map((requirement, index) => <p key={`${requirement}-${index}`} className="flex gap-3 text-sm leading-6 text-slate-700"><ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-[#71931b]" />{requirement}</p>)}</div></section>
            </div>

            <section className="mt-8 border border-slate-200 bg-white p-6"><p className="text-xs font-black uppercase tracking-[0.2em] text-[#71931b]">Ubicación</p><h2 className="mt-2 text-2xl font-black">{venue}</h2><p className="mt-2 text-sm text-slate-600">{venueAddress || 'La dirección exacta será confirmada por la organización.'}</p><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([venueAddress, city].filter(Boolean).join(', '))}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-black text-[#536d12] underline underline-offset-4"><MapPin className="h-4 w-4" />Abrir indicaciones</a><div className="mt-5 grid min-h-44 place-items-center bg-[linear-gradient(135deg,#eef8ca_0%,#f7f3ea_48%,#ffffff_100%)] text-center"><MapPin className="h-7 w-7 text-[#71931b]" /><p className="-mt-8 text-sm font-semibold text-slate-600">{city}</p></div></section>
            <section className="mt-8 border-t border-slate-200 pt-7"><p className="text-xs font-black uppercase tracking-[0.2em] text-[#71931b]">También te puede interesar</p><h2 className="mt-2 text-2xl font-black">Seguí descubriendo la agenda PEEK PASS.</h2><button type="button" onClick={onGoBack} className="mt-4 border-b-2 border-black pb-1 text-sm font-black transition hover:border-[#71931b] hover:text-[#536d12]">Ver eventos relacionados</button></section>
          </section>

          <aside className="h-fit border border-slate-200 bg-white p-6 shadow-[0_22px_50px_-38px_rgba(15,23,42,0.4)] lg:sticky lg:top-28"><p className="text-xs font-black uppercase tracking-[0.22em] text-[#71931b]">Entradas</p><div className="mt-5 flex items-end justify-between gap-3"><div><p className="text-sm text-slate-600">Desde</p><p className="mt-1 text-3xl font-black tracking-[-0.06em]">{formatPrice(price, pack.currency)}</p></div><span className="border border-[#c8df70] bg-[#f4fbd9] px-2.5 py-1 text-xs font-black text-[#536d12]">{mode}</span></div>{discount ? <p className="mt-2 text-sm text-slate-400 line-through">{formatPrice(pack.price, pack.currency)}</p> : null}<div className="mt-6 border-t border-slate-200 pt-5"><p className="text-sm font-black">{day} {month} {year}</p><p className="mt-1 text-sm text-slate-600">{venue}</p></div><button type="button" onClick={handleAddToCart} disabled={isAddingToCart || !!pack.isFallback} className="mt-6 w-full bg-black px-5 py-4 text-sm font-black text-white transition hover:bg-[#536d12] disabled:cursor-not-allowed disabled:bg-slate-400">{isAddingToCart ? 'Agregando...' : getCurrentUser() ? 'Comprar entrada' : 'Ingresar para comprar'}</button><button type="button" onClick={onGoBack} className="mt-3 w-full border border-slate-300 px-5 py-3 text-sm font-bold transition hover:border-black">Volver</button></aside>
        </div>
      </main>
    </div>
  );
};

export default SingleCard;
